import { getAccountScopedLocalStorageKey } from '../../../utils/utils';
import { getCEFRFromIELTS } from '../../../utils/cefrUtils';

export const TOEFL_PROPOSAL_DISMISS_KEY = 'toefl_next_step_dismissed';
export const TOEFL_SETTINGS_PATH = '/settings?tab=toefl';

const C1_PLUS = new Set(['C1', 'C2']);
const OPEN_TOEFL_STATUSES = new Set(['active', 'needs_diagnostic', 'scheduled']);

export function normalizeCefrCode(value) {
  if (!value && value !== 0) return null;
  if (typeof value === 'object') {
    return normalizeCefrCode(value.code || value.current_level || value.currentLevel);
  }
  const raw = String(value).trim().toUpperCase();
  if (C1_PLUS.has(raw) || ['A1', 'A2', 'B1', 'B2'].includes(raw)) return raw;
  const match = raw.match(/\b(A1|A2|B1|B2|C1|C2)\b/);
  return match ? match[1] : null;
}

export function isCefrC1Plus(value) {
  return C1_PLUS.has(normalizeCefrCode(value));
}

export function isIeltsBandC1Plus(bandScore) {
  const score = Number(bandScore);
  if (!Number.isFinite(score)) return false;
  return isCefrC1Plus(getCEFRFromIELTS(score));
}

export function getJourneyCompletion(journey, progress) {
  if (progress && Number.isFinite(Number(progress.daysProgress))) {
    const total = Number(progress.totalDays) || 0;
    const completed = Number(progress.completedDays) || 0;
    const daysProgress = Number(progress.daysProgress);
    return {
      complete: daysProgress >= 100 || (total > 0 && completed >= total),
      daysProgress,
    };
  }

  const days = Array.isArray(journey?.journey)
    ? journey.journey
    : Array.isArray(journey)
      ? journey
      : [];
  if (!days.length) {
    return { complete: false, daysProgress: 0 };
  }
  const completed = days.filter((day) => day?.status === 'completed').length;
  return {
    complete: completed >= days.length,
    daysProgress: Math.round((completed / days.length) * 100),
  };
}

export function isToeflPathAlreadyOpen(bootcamp) {
  const status = String(bootcamp?.status || '').toLowerCase();
  return OPEN_TOEFL_STATUSES.has(status);
}

export function isToeflProposalDismissed() {
  const key = getAccountScopedLocalStorageKey(TOEFL_PROPOSAL_DISMISS_KEY);
  if (!key) return false;
  return localStorage.getItem(key) === '1';
}

export function dismissToeflProposal() {
  const key = getAccountScopedLocalStorageKey(TOEFL_PROPOSAL_DISMISS_KEY);
  if (!key) return;
  localStorage.setItem(key, '1');
}

export function readStoredLanguageJourney(email) {
  if (!email) return null;
  try {
    return JSON.parse(localStorage.getItem(`languageJourney_${email}`) || 'null');
  } catch {
    return null;
  }
}

export function resolveToeflProposal({
  journey,
  progress,
  currentLevel,
  bandScore,
  bootcamp,
  dismissed,
} = {}) {
  if (dismissed || isToeflPathAlreadyOpen(bootcamp)) {
    return { show: false, reason: null };
  }

  const journeyComplete = getJourneyCompletion(journey, progress).complete;
  const reachedC1 =
    isCefrC1Plus(currentLevel) ||
    isCefrC1Plus(journey?.currentLevel) ||
    isIeltsBandC1Plus(bandScore);

  if (journeyComplete) return { show: true, reason: 'journey_complete' };
  if (reachedC1) return { show: true, reason: 'reached_c1' };
  return { show: false, reason: null };
}
